
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import { generateLinkedInDraft, publishToLinkedIn } from '../../utils/linkedinApi';
import { toast } from 'react-toastify';

interface CreatePostModalProps {
    open: boolean;
    onClose: () => void;
    initialTopic?: string;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({ open, onClose, initialTopic }) => {
    const [step, setStep] = useState<'details' | 'draft'>('details');
    const [topic, setTopic] = useState(initialTopic || '');
    const [tone, setTone] = useState('Professional');
    const [extraContext, setExtraContext] = useState('');
    const [draftText, setDraftText] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset state when opening
    React.useEffect(() => {
        if (open) {
            setTopic(initialTopic || '');
            setStep('details');
            setDraftText('');
            setExtraContext('');
            setTone('Professional');
        }
    }, [open, initialTopic]);

    const handleGenerateDraft = async () => {
        setLoading(true);
        try {
            const response = await generateLinkedInDraft({
                topic,
                tone,
                extra_context: extraContext
            });
            if (response.ok) {
                const data = await response.json();
                setDraftText(data.draft_text);
                setStep('draft');
            } else {
                toast.error("Failed to generate draft");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred generating draft");
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        setLoading(true);
        try {
            const response = await publishToLinkedIn({ text: draftText });
            if (response.ok) {
                toast.success("Post published successfully to LinkedIn!");
                onClose();
            } else {
                toast.error("Failed to publish post");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred publishing post");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{step === 'details' ? 'Create LinkedIn Post' : 'Review & Publish'}</DialogTitle>
            <DialogContent dividers>
                {step === 'details' ? (
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <TextField
                            label="Topic"
                            fullWidth
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Tone</InputLabel>
                            <Select
                                value={tone}
                                label="Tone"
                                onChange={(e) => setTone(e.target.value)}
                            >
                                <MenuItem value="Professional">Professional</MenuItem>
                                <MenuItem value="Witty">Witty</MenuItem>
                                <MenuItem value="Inspirational">Inspirational</MenuItem>
                                <MenuItem value="Educational">Educational</MenuItem>
                                <MenuItem value="Casual">Casual</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Personal Touch / Context"
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="Add any specific details, anecdotes, or context..."
                            value={extraContext}
                            onChange={(e) => setExtraContext(e.target.value)}
                        />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <TextField
                            label="Draft Content"
                            multiline
                            rows={10}
                            fullWidth
                            value={draftText}
                            onChange={(e) => setDraftText(e.target.value)}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                {step === 'details' ? (
                    <Button
                        onClick={handleGenerateDraft}
                        variant="contained"
                        disabled={loading || !topic}
                        color="primary"
                    >
                        {loading ? <CircularProgress size={24} /> : 'Generate Draft'}
                    </Button>
                ) : (
                    <>
                        <Button onClick={() => setStep('details')} disabled={loading}>Back</Button>
                        <Button
                            onClick={handlePublish}
                            variant="contained"
                            color="primary"
                            disabled={loading || !draftText}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Publish to LinkedIn'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreatePostModal;
